import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { publicFormsApi, fileUrl } from "@/lib/api";
import { SignaturePad } from "@/components/SignaturePad";
import { COUNTRIES, isMultiFile } from "@/lib/formFields";

const input =
  "w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-gold focus:ring-2 focus:ring-gold/20";

export function FormFieldInput({ field, value, files, onChange, onFiles }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const tid = `field-${field.id}`;
  const cfg = field.config || {};
  const list = files || [];

  const doUpload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await publicFormsApi.upload(fd, (e) =>
      setProgress(Math.round((e.loaded * 100) / (e.total || e.loaded || 1)))
    );
    return { filename: file.name, url: res.url, content_type: res.content_type, size: file.size };
  };

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    const allowed = (cfg.allowed_types || []).map((a) => a.toLowerCase());
    const maxMb = Number(cfg.max_size_mb) || 0;
    const maxFiles = Number(cfg.max_files) || 0;
    const multi = isMultiFile(field.type);
    let results = multi ? [...list] : [];
    setUploading(true);
    try {
      for (const file of arr) {
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        if (allowed.length && !allowed.includes(ext)) {
          toast.error(`${file.name}: file type not allowed`);
          continue;
        }
        if (maxMb && file.size > maxMb * 1024 * 1024) {
          toast.error(`${file.name}: exceeds ${maxMb} MB`);
          continue;
        }
        if (multi && maxFiles && results.length >= maxFiles) {
          toast.error(`Maximum ${maxFiles} files allowed`);
          break;
        }
        setProgress(0);
        try {
          results.push(await doUpload(file));
        } catch {
          toast.error(`${file.name}: upload failed`);
        }
      }
      onFiles(field.id, multi ? results : results.slice(-1));
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeFile = (idx) => onFiles(field.id, list.filter((_, i) => i !== idx));

  const handleSignature = async (dataUrl) => {
    if (!dataUrl) {
      onFiles(field.id, []);
      return;
    }
    setUploading(true);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const res = await doUpload(new File([blob], "signature.png", { type: "image/png" }));
      onFiles(field.id, [res]);
    } catch {
      toast.error("Could not save signature");
    } finally {
      setUploading(false);
    }
  };

  // ---- text-like ----
  if (["short_text", "email", "phone", "number"].includes(field.type)) {
    const type = field.type === "email" ? "email" : field.type === "phone" ? "tel" : field.type === "number" ? "number" : "text";
    return (
      <input
        data-testid={tid}
        type={type}
        className={input}
        placeholder={field.placeholder || ""}
        value={value || ""}
        min={field.type === "number" ? cfg.min : undefined}
        max={field.type === "number" ? cfg.max : undefined}
        maxLength={cfg.maxlength || undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (field.type === "long_text") {
    return (
      <textarea
        data-testid={tid}
        className={input}
        rows={4}
        placeholder={field.placeholder || ""}
        value={value || ""}
        maxLength={cfg.maxlength || undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (field.type === "date" || field.type === "time") {
    return (
      <input
        data-testid={tid}
        type={field.type}
        className={input}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (field.type === "dropdown" || field.type === "country") {
    const opts = field.type === "country" ? COUNTRIES : field.options || [];
    return (
      <select data-testid={tid} className={input} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">{field.placeholder || "Select…"}</option>
        {opts.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }

  if (field.type === "radio") {
    return (
      <div className="space-y-2" data-testid={tid}>
        {(field.options || []).map((o) => (
          <label key={o} className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-white px-4 py-2.5 text-sm hover:border-gold">
            <input type="radio" name={field.id} checked={value === o} onChange={() => onChange(o)} className="accent-[hsl(var(--gold))]" data-testid={`${tid}-opt-${o}`} />
            {o}
          </label>
        ))}
      </div>
    );
  }

  if (field.type === "checkbox" || field.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div className="space-y-2" data-testid={tid}>
        {(field.options || []).map((o) => (
          <label key={o} className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-white px-4 py-2.5 text-sm hover:border-gold">
            <input type="checkbox" checked={arr.includes(o)} onChange={() => toggle(o)} className="accent-[hsl(var(--gold))]" data-testid={`${tid}-opt-${o}`} />
            {o}
          </label>
        ))}
      </div>
    );
  }

  if (field.type === "address") {
    const v = value && typeof value === "object" ? value : {};
    const set = (k, val) => onChange({ ...v, [k]: val });
    return (
      <div className="grid gap-2 sm:grid-cols-2" data-testid={tid}>
        <input className={`${input} sm:col-span-2`} placeholder="Street address" value={v.line1 || ""} onChange={(e) => set("line1", e.target.value)} data-testid={`${tid}-line1`} />
        <input className={input} placeholder="City" value={v.city || ""} onChange={(e) => set("city", e.target.value)} data-testid={`${tid}-city`} />
        <input className={input} placeholder="State / Province" value={v.state || ""} onChange={(e) => set("state", e.target.value)} data-testid={`${tid}-state`} />
        <input className={input} placeholder="ZIP / Postal code" value={v.zip || ""} onChange={(e) => set("zip", e.target.value)} data-testid={`${tid}-zip`} />
        <select className={input} value={v.country || ""} onChange={(e) => set("country", e.target.value)} data-testid={`${tid}-country`}>
          <option value="">Country</option>
          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
    );
  }

  if (field.type === "state_city") {
    const v = value && typeof value === "object" ? value : {};
    const set = (k, val) => onChange({ ...v, [k]: val });
    return (
      <div className="grid gap-2 sm:grid-cols-2" data-testid={tid}>
        <input className={input} placeholder="State / Province" value={v.state || ""} onChange={(e) => set("state", e.target.value)} data-testid={`${tid}-state`} />
        <input className={input} placeholder="City" value={v.city || ""} onChange={(e) => set("city", e.target.value)} data-testid={`${tid}-city`} />
      </div>
    );
  }

  if (field.type === "signature") {
    return <SignaturePad testId={tid} onSave={handleSignature} />;
  }

  // ---- file / file_multiple / image ----
  const multi = isMultiFile(field.type);
  const accept = field.type === "image" ? "image/*" : (cfg.allowed_types || []).map((e) => "." + e.toLowerCase()).join(",") || undefined;
  return (
    <div data-testid={tid}>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid={`${tid}-upload-btn`}
        className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-white px-4 py-6 text-sm text-muted-foreground transition-colors hover:border-gold disabled:opacity-60"
      >
        {uploading ? <Loader2 className="h-6 w-6 animate-spin text-gold" /> : <Upload className="h-6 w-6 text-gold" />}
        <span className="font-medium text-foreground">{uploading ? `Uploading… ${progress}%` : `Click to upload ${multi ? "files" : "a file"}`}</span>
        <span className="text-xs">
          {(cfg.allowed_types || ["JPG", "PNG", "PDF"]).join(", ")}
          {cfg.max_size_mb ? ` · max ${cfg.max_size_mb} MB` : ""}
          {multi && cfg.max_files ? ` · up to ${cfg.max_files} files` : ""}
        </span>
      </button>
      {uploading && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-gold transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
      <input ref={fileRef} type="file" accept={accept} multiple={multi} className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid={`${tid}-input`} />
      {list.length > 0 && (
        <div className="mt-3 space-y-2">
          {list.map((f, i) => (
            <div key={i} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-white px-3 py-2 text-sm" data-testid={`${tid}-file-${i}`}>
              <a href={fileUrl(f.url)} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-2 text-foreground">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                <span className="truncate">{f.filename}</span>
              </a>
              <button type="button" onClick={() => removeFile(i)} className="shrink-0 text-muted-foreground hover:text-destructive" data-testid={`${tid}-remove-${i}`}>
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FormFieldInput;
